/**
 * MemoryHeroMoment — overlay that performs the "new snap arrives"
 * choreography on top of any wall surface (MemoryWallPage, VenueLive,
 * preview tile). Per product spec the moment plays as four beats:
 *
 *   1. Materialise (rise + fade-in, scale 1.3× → 1.0×, soft blur clears)
 *   2. Tape drop  (subtle bounce on settle + shadow lands progressively)
 *   3. Settle     (the polaroid breathes once into its final pose)
 *   4. Glow linger (~4s gold halo, surrounding wall dims, then fades)
 *
 * Three intensities — strictly visual, the data flow is identical:
 *
 *   • `tile`      — restrained, smaller, lives inline (host preview)
 *   • `wall`      — phone Memory Wall page
 *   • `projector` — VenueLivePage / shared screen — slower, larger,
 *                   more breathing room, more shadow landing
 *
 * Imperfection rule: ±120ms entry delay, ±2° rotation variance,
 * 3 easing curves picked deterministically per snap_id so multiple
 * hero moments don't read as synchronised CSS — the illusion of
 * physical pinning breaks if every arrival looks pixel-identical.
 *
 * Architecture note: we keep ALL keyframes static (`memoryHero-rise-{
 * intensity}`, `…-settle-{intensity}`, `…-glow`) — none of them
 * reference per-snap values like rotation. Per-snap variance is
 * applied via static CSS variables / inline transforms on a WRAPPING
 * element so the keyframes themselves never need re-injection.
 */

import React, { useMemo } from 'react';
import Polaroid from './Polaroid';

const INTENSITY = {
  tile: {
    width:      120,
    initScale:  1.20,
    riseMs:     420,
    settleMs:   620,
    glowMs:     1400,
    haloPx:     14,
    eyebrow:    null,
  },
  wall: {
    width:      280,
    initScale:  1.30,
    riseMs:     600,
    settleMs:   1000,
    glowMs:     2800,
    haloPx:     22,
    eyebrow:    '✦ A NEW MEMORY',
  },
  projector: {
    width:      420,
    initScale:  1.45,
    riseMs:     1000,
    settleMs:   1400,
    glowMs:     3600,
    haloPx:     34,
    eyebrow:    '✦ A NEW MEMORY JUST ARRIVED',
  },
};

// Total lifetime for the wrapper fade-out — used to size the parent
// `animationDuration`. Caller still drives the unmount via the hook's
// `heroLifetime` so this is purely visual padding.
const totalMs = (cfg) => cfg.riseMs + cfg.settleMs + cfg.glowMs;

// Deterministic-per-snap variance — same snap always gets the same
// delay/rotation/easing so re-renders don't visually jitter.
const variance = (id) => {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return {
    entryDelay:   ((h % 240) - 120),                // ±120ms
    rotationVar:  (((h >>> 5) % 400) - 200) / 100,  // ±2°
    easingPick:   (h >>> 9) % EASINGS.length,
    tapeStyle:    ((h >>> 12) & 1) ? 'washi' : 'pin',
  };
};

const EASINGS = [
  'cubic-bezier(0.16, 1, 0.3, 1)',     // smooth expo-out
  'cubic-bezier(0.22, 1, 0.36, 1)',    // expo-out with a touch more snap
  'cubic-bezier(0.34, 1.18, 0.64, 1)', // very subtle bounce at the end
];

export const MemoryHeroMoment = ({
  snap,
  intensity = 'wall',
  anchor = 'center',                   // 'center' | 'top'
  testId = 'memory-hero-moment',
}) => {
  const cfg = INTENSITY[intensity] || INTENSITY.wall;
  const vary = useMemo(() => variance(snap?.snap_id || ''), [snap?.snap_id]);
  if (!snap) return null;
  const easing = EASINGS[vary.easingPick];
  const baseRotation = (snap.wall?.rotation || 0) * 0.5 + vary.rotationVar;
  const lifetime = totalMs(cfg);

  // Anchor selects where the hero overlay floats. Phone wall + projector
  // share the same coordinate system; pointer-events stays disabled so
  // the underlying tap targets remain reachable through the overlay.
  const anchorStyle = anchor === 'top'
    ? { position: 'absolute', top: '14%', left: '50%', transform: 'translateX(-50%)' }
    : { position: 'absolute', top: '50%',  left: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div
      data-testid={testId}
      style={{
        ...anchorStyle,
        zIndex: 90,
        pointerEvents: 'none',
        opacity: 0,
        animation: `memoryHeroFade ${lifetime}ms ${easing} forwards`,
        animationDelay: `${vary.entryDelay}ms`,
        ['--hero-halo']: `0 0 ${cfg.haloPx * 2}px rgba(212,175,94,0.55)`,
      }}
    >
      {cfg.eyebrow && (
        <div
          data-testid={`${testId}-eyebrow`}
          style={{
            textAlign: 'center', marginBottom: 14,
            fontSize: intensity === 'projector' ? 14 : 10,
            fontWeight: 800, letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: 'rgba(212,175,94,0.92)',
            textShadow: '0 0 18px rgba(212,175,94,0.55)',
            opacity: 0,
            animation: `memoryHeroEyebrow ${cfg.riseMs + cfg.settleMs}ms ${easing} forwards`,
            animationDelay: `${vary.entryDelay + 120}ms`,
          }}
        >{cfg.eyebrow}</div>
      )}

      {/* Rotation wrapper — static transform so the rotation never
          fights the keyframes (which animate scale/translate only). */}
      <div
        style={{ transform: `rotate(${baseRotation}deg)`, transformOrigin: 'center top' }}
      >
        <div
          data-testid={`${testId}-polaroid`}
          style={{
            transform: `scale(${cfg.initScale}) translateY(-${intensity === 'projector' ? 40 : intensity === 'tile' ? 14 : 22}px)`,
            filter: 'blur(4px)',
            animation: `memoryHeroRise-${intensity} ${cfg.riseMs}ms ${easing} forwards, memoryHeroSettle-${intensity} ${cfg.settleMs}ms ${easing} ${cfg.riseMs}ms forwards, memoryHeroGlow ${cfg.glowMs}ms ease-out ${cfg.riseMs + cfg.settleMs}ms forwards`,
          }}
        >
          <Polaroid
            photoUrl={snap.photo_url}
            message={snap.message}
            penColor={snap.pen_hex || snap.pen_color}
            seed={snap.style_seed || 1}
            width={cfg.width}
            tape={vary.tapeStyle}
            showCorners
          />
        </div>
      </div>

      <style>{`
        @keyframes memoryHeroFade {
          0%   { opacity: 0; }
          12%  { opacity: 1; }
          92%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes memoryHeroEyebrow {
          0%   { opacity: 0; transform: translateY(6px); }
          60%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; }
          100% { opacity: 0; }
        }

        /* ── Rise (stage 1): scale + translate the polaroid into view, clear the blur. ── */
        @keyframes memoryHeroRise-tile {
          0%   { transform: scale(${INTENSITY.tile.initScale})   translateY(-14px); filter: blur(3px); }
          100% { transform: scale(1.04)                          translateY(-2px);  filter: blur(0); }
        }
        @keyframes memoryHeroRise-wall {
          0%   { transform: scale(${INTENSITY.wall.initScale})   translateY(-22px); filter: blur(4px); }
          100% { transform: scale(1.06)                          translateY(-4px);  filter: blur(0); }
        }
        @keyframes memoryHeroRise-projector {
          0%   { transform: scale(${INTENSITY.projector.initScale}) translateY(-40px); filter: blur(6px); }
          100% { transform: scale(1.08)                              translateY(-6px);  filter: blur(0); }
        }

        /* ── Settle (stage 2 + 3): the polaroid breathes once into its
              final pose. Tape "drops" via the slight downward overshoot,
              shadow lands progressively as the halo deepens. ── */
        @keyframes memoryHeroSettle-tile {
          0%   { transform: scale(1.04) translateY(-2px); filter: drop-shadow(0 6px 12px rgba(0,0,0,0.4)); }
          70%  { transform: scale(0.99) translateY(2px);  filter: drop-shadow(0 10px 16px rgba(0,0,0,0.42)) drop-shadow(0 0 16px rgba(212,175,94,0.32)); }
          100% { transform: scale(1.0)  translateY(0);    filter: drop-shadow(0 12px 18px rgba(0,0,0,0.45)) drop-shadow(0 0 ${INTENSITY.tile.haloPx}px rgba(212,175,94,0.36)); }
        }
        @keyframes memoryHeroSettle-wall {
          0%   { transform: scale(1.06) translateY(-4px); filter: drop-shadow(0 10px 18px rgba(0,0,0,0.42)); }
          70%  { transform: scale(0.98) translateY(4px);  filter: drop-shadow(0 16px 26px rgba(0,0,0,0.45)) drop-shadow(0 0 22px rgba(212,175,94,0.4)); }
          100% { transform: scale(1.0)  translateY(0);    filter: drop-shadow(0 18px 30px rgba(0,0,0,0.48)) drop-shadow(0 0 ${INTENSITY.wall.haloPx}px rgba(212,175,94,0.46)); }
        }
        @keyframes memoryHeroSettle-projector {
          0%   { transform: scale(1.08) translateY(-6px); filter: drop-shadow(0 14px 26px rgba(0,0,0,0.45)); }
          70%  { transform: scale(0.97) translateY(6px);  filter: drop-shadow(0 22px 36px rgba(0,0,0,0.48)) drop-shadow(0 0 30px rgba(212,175,94,0.5)); }
          100% { transform: scale(1.0)  translateY(8px);  filter: drop-shadow(0 28px 44px rgba(0,0,0,0.5)) drop-shadow(0 0 ${INTENSITY.projector.haloPx}px rgba(212,175,94,0.55)); }
        }

        /* ── Glow linger (stage 4): the gold halo holds, then fades. ── */
        @keyframes memoryHeroGlow {
          0%   { filter: drop-shadow(0 18px 30px rgba(0,0,0,0.48)) drop-shadow(0 0 36px rgba(212,175,94,0.5)); }
          78%  { filter: drop-shadow(0 18px 30px rgba(0,0,0,0.48)) drop-shadow(0 0 26px rgba(212,175,94,0.36)); }
          100% { filter: drop-shadow(0 14px 22px rgba(0,0,0,0.45)) drop-shadow(0 0 0 rgba(212,175,94,0)); }
        }
      `}</style>
    </div>
  );
};

export default MemoryHeroMoment;
